
import SafeMath from 'common/safeMath';
import { observable, computed, action, makeObservable } from 'mobx';
import { Format } from 'nomst/Stores/Utility/format';
import { IManagersCountryBudget } from 'typings/managersCountryBudget';
import { ManagerBudget } from 'nomst/Stores/Budget/managerBudget';
import { currencyConversion } from 'utils/MobxDataManager/singletons';
import * as formatParse from 'common/format-parse';

const sum = (accumulator: number, value: number) => accumulator + value;

export class ManagersCountryBudget {
    @observable countryCode: string;
    @observable isoCurrencyCode: string;
    @observable managers: ManagerBudget[];
    @observable isUnitValue: boolean;
    @observable groupByFlag?: string;
    @observable targetGroupedBudgetPercentageInput: string;
    @observable targetGroupedBudgetPercentage: number;
    @observable targetGroupedBudgetValueInput: string;
    @observable targetGroupedBudgetValue: number;
    @observable isTargetGroupedBudgetPercentageUpdated: boolean;
    @observable isTargetGroupedBudgetValueUpdated: boolean;
    _format: Format;

    constructor(dto: IManagersCountryBudget, format: Format) {
        makeObservable(this);
        this.countryCode = dto.countryCode;
        this.isoCurrencyCode = dto.isoCurrencyCode;
        this.managers = dto.managers;
        this.isUnitValue = dto.isUnitValue;
        this.groupByFlag = dto.groupByFlag;
        this._format = format;
        this.isTargetGroupedBudgetPercentageUpdated = false;
        this.isTargetGroupedBudgetValueUpdated = false;

        this.updateBudgetPercentagePerGroupedManagers = this.updateBudgetPercentagePerGroupedManagers.bind(this);
        this.updateBudgetValuePerGroupedManagers = this.updateBudgetValuePerGroupedManagers.bind(this);
        this.turnOffTargetBudgetPercentageUpdateFlag = this.turnOffTargetBudgetPercentageUpdateFlag.bind(this);
        this.turnOffTargetBudgetValueUpdateFlag = this.turnOffTargetBudgetValueUpdateFlag.bind(this);
    }
    
    @computed
    get totalCurrentPay(): number {
        return this.managers.map(manager => manager.employeesTotalCurrentPay).reduce(sum, 0);
    }

    @computed
    get totalProratedCurrentPay(): number {
        return this.managers.map(manager => manager.employeesTotalProratedCurrentPay).reduce(sum, 0);
    }

    @computed
    get originalBudget(): number {
        return this.managers.map(manager => manager.originalBudget).reduce(sum, 0);
    }

    @computed
    get adjustedBudget(): number {
        return this.managers.map(manager => manager.adjustedBudget).reduce(sum, 0);
    }

    @computed
    get adjustedBudgetBeforeProration(): number {
        return this.managers.map(manager => manager.adjustedBudgetBeforeProration).reduce(sum, 0);
    }

    @computed
    get proratedBudget(): number {
        return this.managers.map(manager => manager.proratedBudget).reduce(sum, 0);
    }

    @computed
    get totalSpent(): number {
        return this.managers.map(manager => manager.spent).reduce(sum, 0);
    }

    getConvertedValueToDisplay(value: number): string {
        return (this.isUnitValue)
            ? this._format.formatStockDecimal(value)
            : this._format.formatCurrencyValueAbbreviated(this.getCurrencyCodeToUse(),
                currencyConversion.convertCurrency(
                    this.isoCurrencyCode,
                    this.getCurrencyCodeToUse(),
                    value));
    }

    getCurrencyCodeToUse(): string {
        if (!currencyConversion.selectedCurrency || currencyConversion.selectedCurrency === 'Multiple') {
            return this.isoCurrencyCode;
        }
        return currencyConversion.selectedCurrency;
    }

    @computed
    get adjustedBudgetPercentage(): number {
        return SafeMath.divide(this.adjustedBudget, this.totalCurrentPay);
    }

    @computed
    get budgetBalance(): number {
        return this.adjustedBudget - this.totalSpent;
    }

    @computed
    get totalCurrentPayText(): string {
        return this._format.formatCurrencyValueAbbreviated(this.getCurrencyCodeToUse(),
            currencyConversion.convertCurrency(
                this.isoCurrencyCode,
                this.getCurrencyCodeToUse(),
                this.totalCurrentPay));
    }

    @computed
    get originalBudgetText(): string {
        return this.getConvertedValueToDisplay(this.originalBudget);
    }

    @computed
    get adjustedBudgetText(): string {
        if(this.isTargetGroupedBudgetValueUpdated) {
            return this.targetGroupedBudgetValueInput;
        }
        
        return this.getConvertedValueToDisplay(this.adjustedBudget);
    }

    @computed
    get adjustedBudgetBeforeProrationText(): string {
        return this.getConvertedValueToDisplay(this.adjustedBudgetBeforeProration);
    }

    @computed
    get totalSpentText(): string {
        return this.getConvertedValueToDisplay(this.totalSpent);
    }

    @computed
    get adjustedBudgetPercentageText(): string {
        if(this.isTargetGroupedBudgetPercentageUpdated) {
            return this.targetGroupedBudgetPercentageInput;
        }
        
        return this._format.formatDecimalToPercentage(this.adjustedBudgetPercentage);
    }

    @computed
    get proratedBudgetText(): string {
        return this.getConvertedValueToDisplay(this.proratedBudget);
    }

    @computed
    get budgetBalanceText(): string {
        const budgetBalance = Math.abs(this.budgetBalance);
        const text = this.getConvertedValueToDisplay(budgetBalance);
        return (this.budgetBalance < 0) ? `(${text})` : text;
    }

    @computed
    get budgetBalanceClass(): string {
        return (this.budgetBalance < 0) ? 'pxl-text-c10' : '';
    }

    @action
    turnOffTargetBudgetPercentageUpdateFlag() {
        this.isTargetGroupedBudgetPercentageUpdated = false;
    }

    @action
    setTargetBudgetPercentage(value: string) {
        this.targetGroupedBudgetPercentageInput = formatParse.stripNonPercentage(value);
        this.targetGroupedBudgetPercentage = formatParse.parseValueOrDefault(value, 0);
        this.isTargetGroupedBudgetPercentageUpdated = true;
    }

    @action
    onBlurTargetBudgetPercentage(): void {
        this.updateBudgetPercentagePerGroupedManagers(this.targetGroupedBudgetPercentage);
    }

    @action
    turnOffTargetBudgetValueUpdateFlag() {
        this.isTargetGroupedBudgetValueUpdated = false;
    }

    @action
    setTargetBudgetValue(value: string) {
        this.targetGroupedBudgetValueInput = value;
        this.targetGroupedBudgetValue = formatParse.parseValueOrDefault(value, 0);
        this.isTargetGroupedBudgetValueUpdated = true;
    }

    @action
    onBlurTargetBudgetValue(): void {
        this.updateBudgetValuePerGroupedManagers(this.targetGroupedBudgetValue);
    }

    @action
    pushManagerBudgets(managerBudgets: ManagerBudget[]): void {
        this.managers.push(...managerBudgets);
    }

    @action
    updateBudgetPercentagePerGroupedManagers(percentage: number): void {
        this.managers.forEach((manager) => {
            manager.changeBudgetByPercentage(percentage, true);
        });
    }

    @action
    updateBudgetValuePerGroupedManagers(newValue: number): void {
        const count = this.managers.length;
        const budgetForEachManager = SafeMath.divide(newValue, count);
        this.managers.forEach((manager) => {
            manager.changeBudgetByValue(budgetForEachManager, true);
        });
    }
}